// src/pages/PrivacyPage.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageBack } from '../components/shared/PageBack';

const Section = ({ title, children }) => (
  <div style={s.section}>
    <div style={s.sectionTitle}>{title}</div>
    {children}
  </div>
);

const PrivacyPage = () => {
  const navigate = useNavigate();
  return (
    <div style={s.page}>
      <button onClick={() => navigate(-1)} style={s.back}>
        <div style={s.backCircle}>←</div>
        <span style={s.backLbl}>Back</span>
      </button>

      <h1 style={s.h1}>Privacy Notice</h1>
      <p style={s.meta}>Plain English · April 2026</p>

      <Section title="Who we are">
        <p style={s.body}>
          Gather is a free community events noticeboard. We are the data controller for any personal information you provide when using Gather.
        </p>
        <p style={s.body}>
          Questions? Contact us at <a href="mailto:hello@gatheryourcommunity.com" style={s.link}>hello@gatheryourcommunity.com</a>
        </p>
      </Section>

      <Section title="What we collect and why">
        {[
          ['Email address', 'To create your account. We do not use your email for marketing without your consent.'],
          ['Name', 'So we can address you properly and so organisers can identify RSVPs.'],
          ['Your role', 'To show you the right features — organisers see the Add Event button, members see browsing options.'],
          ['Profile photo (optional)', 'Entirely your choice. Not required. Appears on your profile only. Remove it any time.'],
          ['Events you post (organisers)', 'To display your events on the Gather board so the community can find them.'],
          ['RSVPs you make', 'To let event organisers know who is planning to attend. Visible to the organiser and Gather admin only — not to other community members.'],
        ].map(([item, reason]) => (
          <div key={item} style={s.tableRow}>
            <div style={s.tableKey}>{item}</div>
            <div style={s.tableVal}>{reason}</div>
          </div>
        ))}
      </Section>

      <Section title="What we do NOT do">
        {[
          'We do not sell your data',
          'We do not share it with advertisers',
          'We do not use it for profiling or targeted advertising',
          'There are no ads on Gather',
          'We do not share your email address with other users or third parties',
          'We do not store payment information — Gather is free',
        ].map(item => (
          <div key={item} style={s.bullet}>
            <span style={s.bulletDot}>✕</span>
            <span style={s.bulletText}>{item}</span>
          </div>
        ))}
      </Section>

      <Section title="Who can see your information">
        {[
          ['Your name', 'Gather admin only — not visible to other users or the public'],
          ['Your email', 'Gather admin only — never shared'],
          ['Your profile photo', 'Visible on your profile to organisers whose events you have RSVPed to'],
          ['Your RSVPs', 'The event organiser and Gather admin only — not other community members'],
          ['Events you post', 'Visible to everyone — this is the purpose of posting an event'],
        ].map(([item, who]) => (
          <div key={item} style={s.tableRow}>
            <div style={s.tableKey}>{item}</div>
            <div style={s.tableVal}>{who}</div>
          </div>
        ))}
      </Section>

      <Section title="How long we keep your information">
        <p style={s.body}>
          We keep your information for as long as you have an active account. If you would like your account and all associated information deleted, contact us and we will remove it within 5 working days.
        </p>
      </Section>

      <Section title="Your rights (GDPR)">
        {[
          ['Access', 'Request the information we hold about you — just ask and we will send it.'],
          ['Correct', 'Ask us to fix any information that is wrong.'],
          ['Delete', 'Contact us to remove your account and all data.'],
          ['Withdraw consent', 'Stop using Gather and request deletion whenever you like.'],
        ].map(([right, detail]) => (
          <div key={right} style={s.bullet}>
            <span style={{ ...s.bulletDot, color: '#1A7A4A' }}>✓</span>
            <span style={s.bulletText}><strong>{right}</strong> — {detail}</span>
          </div>
        ))}
        <p style={{ ...s.body, marginTop: 12 }}>
          To exercise any of these rights, email <a href="mailto:hello@gatheryourcommunity.com" style={s.link}>hello@gatheryourcommunity.com</a>. We will respond within 5 working days.
        </p>
      </Section>

      <Section title="Cookies and browsing">
        <p style={s.body}>
          You do not need an account to browse Gather. No personal information is collected from visitors who simply browse. Gather uses standard session cookies to keep you logged in. No tracking cookies or advertising cookies are used.
        </p>
      </Section>

      <Section title="Questions or concerns">
        <p style={s.body}>
          Contact Grace Mulvihill, Founder at <a href="mailto:hello@gatheryourcommunity.com" style={s.link}>hello@gatheryourcommunity.com</a>
        </p>
        <p style={s.body}>
          If you are not satisfied with our response, you have the right to contact the <a href="https://www.dataprotection.ie" target="_blank" rel="noopener noreferrer" style={s.link}>Data Protection Commission Ireland</a> at dataprotection.ie or by calling 01 765 0100.
        </p>
      </Section>

      <div style={s.footer}>Gather · Loughrea, Co. Galway · April 2026</div>
      <PageBack position="bottom" fallbackPath="/profile" />
    </div>
  );
};

const s = {
  page: { background: '#fff', minHeight: '100vh', padding: '52px 24px 60px', maxWidth: 430, margin: '0 auto' },
  back: { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 24 },
  backCircle: { width: 34, height: 34, background: '#F8FBF8', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: '#2C4A2E' },
  backLbl: { fontSize: 13, color: '#4A6A4A', fontWeight: 600 },
  h1: { fontSize: 28, fontWeight: 800, color: '#1A2E1A', marginBottom: 4, letterSpacing: '-0.3px' },
  meta: { fontSize: 13, color: '#7A9A7A', marginBottom: 32, fontWeight: 500 },
  section: { marginBottom: 28 },
  sectionTitle: { fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: '#1A7A4A', marginBottom: 10 },
  body: { fontSize: 14, color: '#3A5A3A', lineHeight: 1.7, margin: '0 0 8px 0' },
  link: { color: '#1A7A4A', fontWeight: 600 },
  tableRow: { display: 'flex', flexDirection: 'column', background: '#F8FBF8', borderRadius: 10, padding: '10px 12px', marginBottom: 6 },
  tableKey: { fontSize: 12, fontWeight: 700, color: '#1A2E1A', marginBottom: 3 },
  tableVal: { fontSize: 13, color: '#3A5A3A', lineHeight: 1.5 },
  bullet: { display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8 },
  bulletDot: { fontSize: 12, color: '#C0392B', fontWeight: 700, flexShrink: 0, marginTop: 2 },
  bulletText: { fontSize: 14, color: '#3A5A3A', lineHeight: 1.5 },
  footer: { textAlign: 'center', fontSize: 12, color: '#7A9A7A', marginTop: 32, paddingTop: 16, borderTop: '1px solid #EBF2EB' },
};

export default PrivacyPage;
